import Actions from 'Actions';
import OrderActions from 'actions/OrderActions';
import klarnaUtils from 'utils/Klarna';
import decorators from 'utils/decorators';
import { INTERSTICE_DELAY_MS } from 'constants/Checkout';

import {
    SHOW_ERROR,
    TOGGLE_SHIPPING,
    TOGGLE_SELECTED,
    SET_READY,
    SET_KLARNA_ORDER,
    SET_KLARNA_EMD_ENABLED,
    SET_INITIALIZING
} from 'constants/actionTypes/klarna';

function toggleSelect(isSelected) {
    return {
        type: TOGGLE_SELECTED,
        isSelected
    };
}

function showError(errorMessage, fireAnalytics = false) {
    return {
        type: SHOW_ERROR,
        error: {
            errorMessage,
            fireAnalytics
        }
    };
}

function setReady(readyStatus) {
    return {
        type: SET_READY,
        isReady: readyStatus
    };
}

function setInitializing(isInitializing) {
    return {
        type: SET_INITIALIZING,
        isInitializing
    };
}

function setKlarnaOrder(klarnaOrder) {
    return {
        type: SET_KLARNA_ORDER,
        klarnaOrder
    };
}

function setKlarnaEmdEnabled(klarnaEmdEnabled) {
    return {
        type: SET_KLARNA_EMD_ENABLED,
        klarnaEmdEnabled
    };
}

function toggleShipping(useMyShippingAddress) {
    return {
        type: TOGGLE_SHIPPING,
        useMyShippingAddress
    };
}

/**
 * Persists the Klarna Extra Merchant Data (EMD) from a createSession response into the
 * Redux klarna data.
 * This is what enables client-side EMD enrichment (loyalty_level, account_registration_date) in
 * klarnaUtils.getOrderPayload.
 */
const persistEmdFromSession = session => dispatch => {
    const klarnaEmdEnabled = session?.klarnaEmdEnabled;
    const extraMerchantData = session?.klarnaOrder?.attachment?.body;

    if (klarnaEmdEnabled && extraMerchantData) {
        try {
            dispatch(setKlarnaOrder(JSON.parse(extraMerchantData)));
            dispatch(setKlarnaEmdEnabled(klarnaEmdEnabled));
        } catch (error) {
            Sephora.logger.error('Failed to parse Klarna EMD data', error);
        }
    }
};

const backgroundInit = errorMessage => dispatch => {
    dispatch(setReady(false));
    dispatch(setInitializing(true));
    decorators
        .withInterstice(klarnaUtils.initPaymentGroup, INTERSTICE_DELAY_MS)()
        .then(({ session, order }) => {
            dispatch(OrderActions.updateOrder(order));
            dispatch(persistEmdFromSession(session));

            klarnaUtils
                .load('backgroundPaymentWidget', session.clientToken)
                .then(() => {
                    // SET_READY already sets isInitializing: false in the reducer, no need to dispatch separately
                    dispatch(setReady(true));
                })
                .catch(error => {
                    // eslint-disable-next-line no-console
                    console.error(error);
                    dispatch(showError(errorMessage, true));
                    dispatch(setInitializing(false));
                });
        })
        .catch(error => {
            dispatch(Actions.showInterstice(false));
            //eslint-disable-next-line no-console
            console.error(error);
            dispatch(showError(errorMessage, true));
            dispatch(setInitializing(false));
        });
};

const placeOrder =
    ({ errorMessage, denialMessage }) =>
        (dispatch, getState) => {
            dispatch(Actions.showInterstice(true));
            const { orderDetails } = getState().order;
            const { useMyShippingAddress } = getState().klarna;

            return klarnaUtils
                .authorize(orderDetails, useMyShippingAddress)
                .then(data => {
                    if (data.authorization_token) {
                        return data;
                    } else if (data.approved === false) {
                    // User dismissed the Klarna modal we should treat as cancellation
                    // otherwise we get our place order btn stuck in weird state
                        dispatch(Actions.showInterstice(false));

                        return null;
                    } else {
                    // If we fail to retrieve the authorization token, we return an
                    // empty object so that backend tries to get the authorization token
                    // themselves
                    // eslint-disable-next-line camelcase
                        data.authorization_token = '';

                        return data;
                    }
                })
                .catch(error => {
                    dispatch(Actions.showInterstice(false));
                    const message = error?.type === klarnaUtils.ERROR_TYPES.AUTH_DENIAL && denialMessage ? denialMessage : errorMessage;
                    dispatch(showError(message, true));

                    return null;
                });
        };

export default {
    showError,
    toggleShipping,
    toggleSelect,
    setReady,
    setInitializing,
    backgroundInit,
    persistEmdFromSession,
    setKlarnaOrder,
    setKlarnaEmdEnabled,
    placeOrder
};
